import { selectorFamily } from "recoil"
import { contactDetailRequestIdState } from "src/recoil/atoms/contact/contactDetailRequestIdState"
import { contactDetailCacheSkipState } from "src/recoil/atoms/contact/contactDetailCacheSkipState"
import {
  getOutBoxContactDetail,
  getMigrationContact,
  getReservedContact,
  Item,
  getInBoxContactDetail,
} from "src/apis/contact/getContactDetail"
import { tokensState } from "src/recoil/atoms/authorization/tokensState"
import { CustomAlert } from "src/utils/CustomAlert"
import { myProfileSelectorFamily } from "src/recoil/selectors/organization/myProfile/myProfileSelectorFamily"
import { selectedMyOrganizationSelector } from "src/recoil/selectors/organization/selectedMyOrganizationSelector"
import { ContactListCacheService } from "src/services/cache/ContactListCacheService"

export const contactDetailSelectorFamily = selectorFamily<
  Item | undefined,
  { contactId: string | undefined; type: "Inbox" | "Outbox" | "Reserved" | "Migration" }
>({
  key: "selectors/contact/contactDetailSelectorFamily",
  get:
    ({ contactId, type }) =>
    async ({ get }) => {
      get(contactDetailRequestIdState)
      // キャッシュスキップフラグを監視（初回ロード時/Pull-to-Refresh時はtrue）
      const skipCache = get(contactDetailCacheSkipState)
      if (contactId == null) {
        return
      }

      // キャッシュチェック
      const cached = ContactListCacheService.getDetail({
        contactId,
        contactType: type,
      })

      // キャッシュを使用する条件:
      // 1. skipCacheがfalse（初回ロード時/Pull-to-Refresh時はtrue）
      // 2. キャッシュが存在する
      // Note: skipCacheのデフォルト値はtrue。初回ロード完了後にfalseに設定される。
      //       これによりブラウザリロード時は常に最新データをAPIから取得する。
      if (!skipCache && cached) {
        return cached.data
      }

      // キャッシュミス: API呼び出し
      const tokens = get(tokensState)
      const accessToken = tokens?.accessToken
      if (accessToken == null) {
        return
      }
      const organization = get(selectedMyOrganizationSelector)
      const organizationId = organization?.id
      if (organizationId == null) {
        return
      }
      const myProfile = get(myProfileSelectorFamily(organizationId))
      const myMemberId = myProfile?.memberId
      if (myMemberId == null) {
        return
      }

      const result =
        type === "Inbox"
          ? await getInBoxContactDetail({ accessToken, myMemberId, contactId, isSent: false })
          : type === "Outbox"
          ? await getOutBoxContactDetail({ accessToken, myMemberId, contactId, isSent: true })
          : type === "Reserved"
          ? await getReservedContact({ accessToken, contactId })
          : await getMigrationContact({ accessToken, contactId, organizationId })
      if (!result.isOk) {
        CustomAlert.alert("エラー", result.error.message)
        return
      }

      // API呼び出し成功: キャッシュに保存
      ContactListCacheService.setDetail(
        {
          contactId,
          contactType: type,
        },
        result.content
      )

      return result.content
    },
})
